import type { MouseEvent } from 'react';
import AddIcon from '@mui/icons-material/Add';
import { Box, Paper, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CardMenuButton, {
  CARD_MENU_BUTTON_CLASS,
  cardWithMenuSx,
} from './CardMenuButton';

export const SWATCH_CARD_HEIGHT = 96;

export interface SwatchCardProps {
  color: string;
  title: string;
  onClick?: () => void;
  onMenuClick?: (event: MouseEvent<HTMLElement>) => void;
  height?: number;
}

const SwatchCard = ({
  color,
  title,
  onClick,
  onMenuClick,
  height = SWATCH_CARD_HEIGHT,
}: SwatchCardProps) => {
  const theme = useTheme();
  const textColor = theme.palette.getContrastText(color);

  return (
    <Paper
      variant="outlined"
      onClick={onClick}
      sx={{
        ...cardWithMenuSx,
        height,
        display: 'flex',
        alignItems: 'flex-end',
        overflow: 'hidden',
        borderRadius: 2,
        bgcolor: color,
        color: textColor,
        cursor: onClick ? 'pointer' : 'default',
        [`& .MuiIconButton-root.${CARD_MENU_BUTTON_CLASS}`]: {
          color: 'inherit',
        },
        '&:hover': {
          transform: 'none',
          boxShadow: 'none',
          borderColor: 'text.secondary',
        },
      }}
    >
      {onMenuClick ? <CardMenuButton onClick={onMenuClick} /> : null}
      <Typography
        variant="body2"
        fontWeight={600}
        noWrap
        title={title}
        sx={{ px: 1.25, py: 1, width: '100%' }}
      >
        {title}
      </Typography>
    </Paper>
  );
};

export interface SwatchAddCardProps {
  label: string;
  onClick: () => void;
}

export const SwatchAddCard = ({ label, onClick }: SwatchAddCardProps) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    sx={{
      minHeight: SWATCH_CARD_HEIGHT,
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 0.5,
      border: '1px dashed',
      borderColor: 'divider',
      borderRadius: 2,
      bgcolor: 'transparent',
      color: 'text.secondary',
      font: 'inherit',
      cursor: 'pointer',
      '&:hover': {
        borderColor: 'primary.main',
        color: 'primary.main',
        bgcolor: 'action.hover',
      },
    }}
  >
    <AddIcon />
    <Typography variant="caption" fontWeight={500}>
      {label}
    </Typography>
  </Box>
);

export default SwatchCard;
